import { useState } from "react";
import { MenuPresets } from "@/components/MenuPresets";
import { GalleryManager } from "@/components/Gallery";
import { BrandSettings } from "@/components/BrandSettings";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Modal, PageHead, inputCls, saveDoc, useList } from "@/components/kit";
import { api, fmtMoney } from "@/lib/api";

const CATS = ["Menù a persona", "Pacchetto", "Servizio"];
const TYPES = ["Matrimonio", "Cerimonia", "Compleanno", "Aziendale", "Altro"];

function ItemForm({ item, onDone }) {
  const [f, setF] = useState({ nome: "", categoria: "Menù a persona", prezzo: "", prezzo_alta: "", unita: "a persona", descrizione: "", eventi: [], attivo: true, ...item });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const { id, created_at, updated_at, created_by, ...body } = f;
    if (await saveDoc("/catalog", id, { ...body, prezzo: Number(f.prezzo || 0), prezzo_alta: f.prezzo_alta === "" ? "" : Number(f.prezzo_alta) })) onDone();
  };
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Nome *" wide><input className={inputCls} value={f.nome} onChange={set("nome")} data-testid="cat-name" placeholder="es. Menù Gold di mare" /></Field>
        <Field label="Categoria"><select className={inputCls} value={f.categoria} onChange={set("categoria")} data-testid="cat-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label="Unità"><select className={inputCls} value={f.unita} onChange={set("unita")} data-testid="cat-unit"><option>a persona</option><option>forfait</option><option>a pezzo</option></select></Field>
        <Field label="Prezzo bassa stagione / feriale (€)"><input type="number" className={inputCls} value={f.prezzo} onChange={set("prezzo")} data-testid="cat-price" /></Field>
        <Field label="Prezzo alta stagione (mag-set), facoltativo"><input type="number" className={inputCls} value={f.prezzo_alta} onChange={set("prezzo_alta")} data-testid="cat-price-high" /></Field>
        <Field label="Descrizione / cosa comprende" wide><textarea rows={4} className={inputCls} value={f.descrizione} onChange={set("descrizione")} data-testid="cat-desc" /></Field>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        {TYPES.map((t) => <button key={t} onClick={() => setF({ ...f, eventi: f.eventi.includes(t) ? f.eventi.filter((x) => x !== t) : [...f.eventi, t] })} data-testid={`cat-event-${t}`} className={`px-3 py-1.5 rounded-full border ${f.eventi.includes(t) ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "border-[#D4E6EC]"}`}>{t}</button>)}
        <span className="text-[#7B929C] self-center">(nessuno = tutti gli eventi)</span>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.attivo !== false} onChange={(e) => setF({ ...f, attivo: e.target.checked })} data-testid="cat-active" /> Attivo</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.vetrina} onChange={(e) => setF({ ...f, vetrina: e.target.checked })} data-testid="cat-vetrina" /> Mostra nell'area cliente come extra acquistabile</label>
      <Button className="rounded-full" onClick={save} disabled={!f.nome.trim()} data-testid="cat-save">Salva voce</Button>
    </div>
  );
}

function PriceList() {
  const [rows, reload] = useList("/catalog");
  const [edit, setEdit] = useState(null);
  const remove = async () => {
    await api.delete(`/catalog/${edit.id}`);
    setEdit(null);
    reload();
  };
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button className="rounded-full" onClick={() => setEdit({})} data-testid="new-catalog-button"><Plus size={15} className="mr-2" /> Nuova voce</Button>
      </div>
      <p className="text-sm text-[#47606B] max-w-3xl">Le voci del listino si inseriscono nei preventivi con un tocco. In alta stagione (maggio-settembre) si usa il prezzo alta stagione, se indicato. Anche l'assistente che scrive le proposte usa questi prezzi.</p>
      {CATS.map((cat) => {
        const list = (rows || []).filter((r) => r.categoria === cat);
        return (
          <div key={cat} className="space-y-3" data-testid={`catalog-section-${cat}`}>
            <h2 className="font-serif-it text-2xl font-semibold">{cat}</h2>
            {list.length === 0 && <div className="text-sm text-[#7B929C]">Nessuna voce.</div>}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((r) => (
                <button key={r.id} onClick={() => setEdit(r)} className={`text-left rounded-xl border border-[#D4E6EC] bg-white p-4 hover:border-[#0B6E8F] transition-colors ${r.attivo === false ? "opacity-50" : ""}`} data-testid={`catalog-item-${r.id}`}>
                  <div className="flex justify-between gap-2"><span className="font-medium">{r.nome}</span><span className="text-[#0B6E8F] font-semibold whitespace-nowrap">{fmtMoney(r.prezzo)}</span></div>
                  <div className="text-xs text-[#7B929C]">{r.unita}{r.prezzo_alta !== "" && r.prezzo_alta != null ? ` • alta stagione ${fmtMoney(r.prezzo_alta)}` : ""}{(r.eventi || []).length ? ` • ${r.eventi.join(", ")}` : ""}</div>
                  {r.descrizione && <div className="text-xs text-[#47606B] mt-2 line-clamp-3 whitespace-pre-line">{r.descrizione}</div>}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <Modal open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? edit.nome : "Nuova voce di listino"} testId="catalog-dialog">
        {edit && <ItemForm item={edit} onDone={() => { setEdit(null); reload(); }} />}
        {edit?.id && <Button variant="ghost" className="rounded-full text-[#991B1B]" onClick={remove} data-testid="cat-delete"><Trash2 size={14} className="mr-1" /> Elimina</Button>}
      </Modal>
    </div>
  );
}

const TABS = [["menu", "Menù preimpostati"], ["listino", "Listino prezzi"], ["galleria", "Galleria sale"], ["brand", "Loghi e musica"]];

export default function Offer() {
  const [tab, setTab] = useState("menu");
  return (
    <div className="space-y-6">
      <PageHead eyebrow="Menù, prezzi e location" title="Listino" />
      <div className="flex flex-wrap gap-2" data-testid="offer-tabs">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`offer-tab-${k}`} className={`px-4 py-2 rounded-full text-sm border transition-colors ${tab === k ? "bg-[#0B6E8F] text-white border-[#0B6E8F]" : "bg-white border-[#D4E6EC] hover:border-[#0B6E8F]"}`}>{l}</button>)}
      </div>
      {tab === "menu" && <MenuPresets />}
      {tab === "listino" && <PriceList />}
      {tab === "galleria" && <GalleryManager />}
      {tab === "brand" && <BrandSettings />}
    </div>
  );
}

